import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, Clock, Check, Trash2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, PLATFORMS, CONTENT_TYPES, STATUS } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PlatformIcon, MediaThumb } from "@/components/Bits";
import { TranscriptBadge, TranscriptUploadButton, useTranscriptInfo } from "@/components/Transcript";

const TONES = ["Locker & Modern", "Professional B2B", "Inspirierend", "Viral TikTok", "Humorvoll"];

export default function PostEditorDialog({ post, defaultDate, onClose, onSaved }) {
  const { clientId } = useClient();
  const [f, setF] = useState(() => post || { title: "", caption: "", hashtags: [], platforms: ["instagram"], media_ids: [], scheduled_at: defaultDate ? `${defaultDate}T18:00` : "", status: "draft", content_type: "photo" });
  const [files, setFiles] = useState([]);
  const [brief, setBrief] = useState("");
  const [tone, setTone] = useState(TONES[0]);
  const [ai, setAi] = useState(null);
  const [times, setTimes] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/files", { params: { client_id: clientId } }).then((r) => setFiles(r.data)); }, [clientId]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (k, v) => set(k, f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v]);
  const media = files.filter((x) => x.kind !== "text");
  const texts = files.filter((x) => x.kind === "text");
  const uploadTo = async (file, field) => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData(); fd.append("client_id", clientId); fd.append("file", file);
      const r = await api.post("/files", fd);
      setFiles((x) => [r.data, ...x]);
      set(field, r.data.id);
      toast.success("Hochgeladen");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const genCaption = async (tid = f.transcript_id) => {
    setBusy("caption");
    try {
      const r = await api.post("/ai/caption", { client_id: clientId, brief: brief || f.title || "", tone, platform: f.platforms[0] || "instagram", transcript_id: tid || null });
      setAi(r.data);
      if (tid && r.data.captions?.[0]) {
        setF((x) => ({ ...x, caption: r.data.captions[0], hashtags: r.data.hashtags || x.hashtags, title: x.title && x.title !== "Agent schreibt…" ? x.title : (r.data.title || x.title),
          thumbnail_text: r.data.thumbnails?.[0] ? `${r.data.thumbnails[0].word} – ${r.data.thumbnails[0].line}` : x.thumbnail_text }));
        toast.success("Caption aus dem Transkript geschrieben – Variante 1 ist eingesetzt");
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const uploadTranscript = async (file) => {
    if (!file.name.toLowerCase().endsWith(".txt")) return toast.error("Bitte eine .txt-Datei wählen");
    setBusy("upload");
    try {
      const fd = new FormData(); fd.append("client_id", clientId); fd.append("file", file);
      const folder = files.find((x) => f.media_ids.includes(x.id))?.folder_id;
      if (folder) fd.append("folder_id", folder);
      const r = await api.post("/files", fd);
      setFiles((x) => [r.data, ...x]);
      set("transcript_id", r.data.id);
      await genCaption(r.data.id);
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  const trInfo = useTranscriptInfo(f.transcript_id);
  const genTimes = async () => {
    setBusy("times");
    try { setTimes((await api.post("/ai/best-times", { client_id: clientId, platform: f.platforms[0] || "instagram" })).data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save");
    try {
      const body = { ...f, client_id: clientId, hashtags: typeof f.hashtags === "string" ? f.hashtags.split(/\s+/).filter(Boolean) : f.hashtags };
      if (post) await api.put(`/posts/${post.id}`, body); else await api.post("/posts", body);
      toast.success("Post gespeichert");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const remove = async () => { await api.delete(`/posts/${post.id}`); toast.success("Gelöscht"); onSaved(); };

  const hashtagStr = Array.isArray(f.hashtags) ? f.hashtags.join(" ") : f.hashtags;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dark text-foreground max-w-4xl max-h-[92vh] overflow-y-auto scroll-thin" data-testid="post-editor-dialog">
        <DialogHeader><DialogTitle>{post ? "Post bearbeiten" : "Neuer Post"}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3 space-y-4">
            <div className="space-y-1.5"><Label>Titel</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="post-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Format</Label>
                <Select value={f.content_type} onValueChange={(v) => set("content_type", v)}>
                  <SelectTrigger data-testid="post-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CONTENT_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={f.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="post-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Plattformen</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(PLATFORMS).map(([k, p]) => (
                  <button key={k} type="button" onClick={() => toggle("platforms", k)} data-testid={`platform-toggle-${k}`}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${f.platforms.includes(k) ? "border-teal-500 bg-teal-500/15" : "border-border text-muted-foreground hover:border-slate-500"}`}>
                    <PlatformIcon p={k} />{p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Termin</Label><Input type="datetime-local" value={f.scheduled_at || ""} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="post-date-input" /></div>
            <div className="space-y-1.5"><Label>Caption</Label><Textarea rows={6} value={f.caption} onChange={(e) => set("caption", e.target.value)} data-testid="post-caption-input" /></div>
            <div className="space-y-1.5"><Label>Hashtags</Label><Input value={hashtagStr} onChange={(e) => set("hashtags", e.target.value)} data-testid="post-hashtags-input" /></div>
            <div className="space-y-1.5"><Label>Medien aus dem Drive</Label>
              <div className="grid grid-cols-5 gap-2 max-h-44 overflow-y-auto scroll-thin" data-testid="media-picker">
                {media.length === 0 && <p className="col-span-5 text-sm text-muted-foreground">Noch keine Fotos/Videos im Drive.</p>}
                {media.map((file) => (
                  <button key={file.id} type="button" onClick={() => toggle("media_ids", file.id)} className={`relative rounded-md overflow-hidden ring-2 ${f.media_ids.includes(file.id) ? "ring-teal-500" : "ring-transparent"}`} data-testid={`media-pick-${file.id}`}>
                    <MediaThumb file={file} className="aspect-square" />
                    {f.media_ids.includes(file.id) && <span className="absolute top-1 right-1 h-5 w-5 rounded-full bg-teal-500 grid place-items-center"><Check className="h-3 w-3" /></span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Thumbnail</Label>
              <div className="flex gap-2 overflow-x-auto scroll-thin pb-1" data-testid="thumbnail-picker">
                <label className="shrink-0 h-20 w-16 rounded-md border border-dashed border-border grid place-items-center text-muted-foreground cursor-pointer hover:border-teal-500" data-testid="thumbnail-upload">
                  {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadTo(e.target.files?.[0], "thumbnail_id"); e.target.value = ""; }} />
                </label>
                {media.filter((x) => x.kind === "image").map((file) => (
                  <button key={file.id} type="button" onClick={() => set("thumbnail_id", f.thumbnail_id === file.id ? null : file.id)} className={`shrink-0 relative rounded-md overflow-hidden ring-2 ${f.thumbnail_id === file.id ? "ring-amber-400" : "ring-transparent"}`} data-testid={`thumb-pick-${file.id}`}>
                    <MediaThumb file={file} className="h-20 w-16" />
                  </button>
                ))}
              </div>
              <Input value={f.thumbnail_text || ""} onChange={(e) => set("thumbnail_text", e.target.value)} placeholder="Thumbnail-Text, z.B. TEUER – der Fehler beim Textildruck" data-testid="thumbnail-text-input" />
            </div>
          </div>

          <div className="md:col-span-2 space-y-4 rounded-xl border border-purple-500/20 bg-purple-500/[0.04] p-4 h-fit">
            <p className="flex items-center gap-2 text-sm font-medium text-purple-300"><Sparkles className="h-4 w-4" />Agent</p>
            <TranscriptUploadButton onFile={uploadTranscript} busy={busy === "upload" || busy === "caption"} label={f.transcript_id ? "Anderes Transkript (.txt)" : "Transkript (.txt) hochladen"} />
            <TranscriptBadge info={trInfo} />
            {texts.length > 0 && (
              <Select value={f.transcript_id || "none"} onValueChange={(v) => set("transcript_id", v === "none" ? null : v)}>
                <SelectTrigger data-testid="transcript-select"><SelectValue placeholder="Kein Transkript" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Transkript</SelectItem>
                  {texts.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Textarea rows={3} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder={f.transcript_id ? "Transkript ist ausgewählt – optional noch Hinweise ergänzen" : "Worum geht's im Post? Oder oben ein Transkript hochladen"} data-testid="ai-brief-input" />
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => genCaption()} disabled={!!busy} className="w-full bg-purple-600 hover:bg-purple-500 text-white gap-2" data-testid="ai-generate-caption-button">
              {busy === "caption" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}Caption, Hook & Thumbnail-Text
            </Button>
            {ai && (
              <div className="space-y-2" data-testid="ai-caption-results">
                {ai.captions?.map((c, i) => (
                  <button key={i} onClick={() => { set("caption", c); set("hashtags", ai.hashtags || []); }} className="block w-full text-left text-xs p-3 rounded-lg border border-border hover:border-purple-500/60 whitespace-pre-wrap transition-colors" data-testid={`ai-caption-option-${i}`}>{c}</button>
                ))}
                <p className="text-xs text-teal-300">{ai.hashtags?.join(" ")}</p>
                {ai.thumbnails?.length > 0 && <p className="font-mono text-[10px] uppercase tracking-widest text-amber-300 pt-2">Thumbnail-Ideen</p>}
                {ai.thumbnails?.map((t, i) => (
                  <button key={i} onClick={() => set("thumbnail_text", `${t.word} – ${t.line}`)} className="block w-full text-left p-3 rounded-lg border border-amber-400/30 hover:border-amber-400 transition-colors" data-testid={`ai-thumb-option-${i}`}>
                    <span className="block font-display text-xl font-black tracking-tight text-white uppercase">{t.word}</span>
                    <span className="text-xs text-slate-300">{t.line}</span>
                  </button>
                ))}
                {ai.title && !f.title && <button onClick={() => set("title", ai.title)} className="text-xs text-purple-300 underline" data-testid="ai-apply-title">Titel übernehmen: {ai.title}</button>}
              </div>
            )}
            <Button onClick={genTimes} disabled={!!busy} variant="outline" className="w-full gap-2" data-testid="ai-best-times-button">
              {busy === "times" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}Beste Posting-Zeit
            </Button>
            {times && (
              <div className="space-y-2" data-testid="ai-best-times-results">
                {times.slots?.map((s, i) => (
                  <button key={i} onClick={() => set("scheduled_at", s.datetime)} className="block w-full text-left text-xs p-3 rounded-lg border border-border hover:border-teal-500/60 transition-colors" data-testid={`ai-time-option-${i}`}>
                    <span className="font-mono text-teal-300">{new Date(s.datetime).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                    <span className="block text-muted-foreground mt-1">{s.reason}</span>
                  </button>
                ))}
                {times.tip && <p className="text-xs text-muted-foreground">{times.tip}</p>}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-between pt-2">
          {post ? <Button variant="ghost" onClick={remove} className="text-rose-400 gap-2" data-testid="delete-post-button"><Trash2 className="h-4 w-4" />Löschen</Button> : <span />}
          <Button onClick={save} disabled={!!busy} className="bg-teal-500 hover:bg-teal-400 text-slate-950" data-testid="save-post-button">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Speichern"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
